import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useScreenData } from '../hooks/useScreenData';
import RecordList, { ViewSwitch } from '../components/RecordList';
import { EmptyState, ReadError, LoadingState, StaleNotice } from '../components/PageStates';
import { TENANT_COLUMNS } from './TenantList';
import { formatUtc } from './auditFields';
import './Lists.css';

// The four counters GET /platform/api/dashboard returns (platformTenant.service.js getDashboard):
// { asOf, tenants: { total, enabled, locked }, users: { total, enabled, locked } }. "Enabled" is
// is_active, never recent activity (§7.4).
const COUNTERS = [
  ['Tenants', (d) => d.tenants.total, (d) => `Every household, enabled or locked; ${d.tenants.locked} locked.`],
  ['Enabled tenants', (d) => d.tenants.enabled, () => 'Households whose access is not locked.'],
  ['Members', (d) => d.users.total, (d) => `Every member account in every household; ${d.users.locked} locked.`],
  ['Enabled members', (d) => d.users.enabled, () => 'Member accounts that are not locked. This is not a measure of recent use.'],
];
const RECENT = { page: 1, limit: 5, sortBy: 'created_at', sortOrder: 'desc' };

function Section({ title, children }) {
  const id = useId();
  return <section aria-labelledby={id}><h3 id={id}>{title}</h3>{children}</section>;
}

export default function Dashboard() {
  const [tableOnNarrow, setTableOnNarrow] = useState(false);
  // Two requests, each with its own loading, error and stale state: one never speaks for the other.
  const counts = useScreenData('dashboard-counts', (options) => api.getDashboard(options));
  const recent = useScreenData('dashboard-recent', (options) => api.listTenants(RECENT, options),
    { keeps: (data) => data.tenants.length > 0 });

  return (
    <div className="list-page">
      <h2>Dashboard</h2>
      <nav className="shortcuts" aria-label="Dashboard shortcuts">
        <Link to="/tenants">All tenants</Link>
        <Link to="/users">Search members</Link>
        <Link to="/audit">Audit log</Link>
      </nav>

      <Section title="Counts">
        {counts.loading && <LoadingState label="Loading the counts" lines={4} />}
        {counts.error && <ReadError title="Could not load the counts" error={counts.error} onRetry={counts.reload} />}
        {counts.stale && <StaleNotice loadedAt={counts.loadedAt} reason={counts.stale} onReload={counts.reload} />}
        {counts.data && (
          <>
            <dl className="counters">
              {COUNTERS.map(([label, value, definition]) => (
                <div key={label} className="card counter">
                  <dt>{label}</dt>
                  <dd className="count">{value(counts.data)}</dd>
                  <dd className="definition">{definition(counts.data)}</dd>
                </div>
              ))}
            </dl>
            <p className="as-of">Counts as of <time dateTime={counts.data.asOf}>{formatUtc(counts.data.asOf)}</time></p>
          </>
        )}
      </Section>

      <Section title="Recent tenants">
        <ViewSwitch tableOnNarrow={tableOnNarrow} onChange={setTableOnNarrow} />
        {recent.loading && <LoadingState label="Loading recent tenants" />}
        {recent.error && <ReadError title="Could not load recent tenants" error={recent.error} onRetry={recent.reload} />}
        {recent.stale && <StaleNotice loadedAt={recent.loadedAt} reason={recent.stale} onReload={recent.reload} />}
        {recent.data && recent.data.tenants.length === 0 && <EmptyState title="No tenants yet">No household has signed up.</EmptyState>}
        {recent.data && recent.data.tenants.length > 0 && (
          <RecordList tableOnNarrow={tableOnNarrow} label="Recent tenants" caption="The five newest tenants" columns={TENANT_COLUMNS}
            records={recent.data.tenants} recordKey={(t) => t.id} />
        )}
      </Section>
    </div>
  );
}
